import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { T, inputStyle } from "../theme";
import Avatar from "./Avatar";
import LangBadge from "./LangBadge";

export default function AddMemberSheet({
  currentUser,
  existingParticipants, // array of uids already in the chat
  onAdd, // async (newUsers) => void
  onClose,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);

  // Search Firestore users by username prefix
  useEffect(() => {
    if (searchQuery.length < 1) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const q = query(
          collection(db, "users"),
          where("username", ">=", searchQuery.toLowerCase()),
          where("username", "<=", searchQuery.toLowerCase() + "\uf8ff"),
          orderBy("username"),
          limit(20)
        );
        const snap = await getDocs(q);
        const users = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          // Exclude current user AND anyone already in the chat
          .filter(
            (u) =>
              u.uid !== currentUser.uid &&
              !existingParticipants.includes(u.uid)
          );
        setResults(users);
      } catch (err) {
        console.error("User search error:", err);
        setResults([]);
      }
      setSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, currentUser.uid, existingParticipants]);

  const toggle = (u) => {
    setSelected((prev) =>
      prev.find((s) => s.uid === u.uid)
        ? prev.filter((s) => s.uid !== u.uid)
        : [...prev, u]
    );
  };

  const handleAdd = async () => {
    if (selected.length === 0) return;
    setAdding(true);
    try {
      await onAdd(
        selected.map((u) => ({
          uid: u.uid,
          preferredLanguage: u.preferredLanguage || "en",
        }))
      );
      onClose();
    } catch (err) {
      console.error("Add members failed:", err);
    }
    setAdding(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 99,
          animation: "fadeUp 0.15s ease",
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: "75vh",
          background: T.bg1,
          borderRadius: "20px 20px 0 0",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          animation: "fadeUp 0.2s ease",
        }}
      >
        {/* Handle + title */}
        <div style={{ padding: "14px 20px 10px", borderBottom: `1px solid ${T.border}` }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              background: T.border,
              margin: "0 auto 12px",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 17 }}>Add to Chat</span>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: T.textDim,
                fontSize: 22,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <input
            placeholder="Search username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoCapitalize="none"
            autoFocus
            style={{ ...inputStyle, fontSize: 14, marginTop: 12 }}
          />

          {/* Selected chips */}
          {selected.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 10,
                flexWrap: "wrap",
              }}
            >
              {selected.map((u) => (
                <div
                  key={u.uid}
                  onClick={() => toggle(u)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: T.bg3,
                    borderRadius: 20,
                    padding: "4px 10px 4px 4px",
                    cursor: "pointer",
                  }}
                >
                  <Avatar user={u} size={24} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {(u.displayName || u.username).split(" ")[0]}
                  </span>
                  <span style={{ color: T.textDim, fontSize: 14 }}>×</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search results */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 120 }}>
          {searching && (
            <div style={{ textAlign: "center", color: T.textDim, padding: 20, fontSize: 13 }}>
              Searching...
            </div>
          )}
          {!searching && searchQuery.length > 0 && results.length === 0 && (
            <div style={{ textAlign: "center", color: T.textMute, padding: 30, fontSize: 14 }}>
              No new users found for "{searchQuery}"
            </div>
          )}
          {results.map((u) => {
            const sel = selected.find((s) => s.uid === u.uid);
            return (
              <div
                key={u.uid}
                onClick={() => toggle(u)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 20px",
                  borderBottom: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: sel ? T.bg2 : "transparent",
                  transition: "background 0.15s",
                }}
              >
                <Avatar user={u} size={42} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{u.displayName}</div>
                  <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
                    @{u.username} · <LangBadge lang={u.preferredLanguage || "en"} />
                  </div>
                </div>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: `2px solid ${sel ? T.orange : T.border}`,
                    background: sel ? T.orange : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 14,
                  }}
                >
                  {sel ? "✓" : ""}
                </div>
              </div>
            );
          })}
          {!searching && searchQuery.length === 0 && (
            <div style={{ textAlign: "center", color: T.textMute, padding: 30, fontSize: 14, lineHeight: 1.7 }}>
              Search for crew members to add
            </div>
          )}
        </div>

        {/* Add button */}
        {selected.length > 0 && (
          <div
            style={{
              padding: "14px 20px",
              borderTop: `1px solid ${T.border}`,
              background: T.bg1,
            }}
          >
            <button
              onClick={handleAdd}
              disabled={adding}
              style={{
                width: "100%",
                padding: "14px 0",
                background: adding ? T.orangeDim : T.orange,
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontWeight: 900,
                fontSize: 15,
                letterSpacing: 0.5,
                opacity: adding ? 0.7 : 1,
              }}
            >
              {adding
                ? "ADDING..."
                : `ADD ${selected.length} MEMBER${selected.length > 1 ? "S" : ""}`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
